<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { get } from 'svelte/store';
  import { Badge, Column, Input, Spinner, Table, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { SearchFilter } from '@client/components/filters/search';
  import { Select } from '@client/components/select';
  import { Spinner as Loading } from '@client/components/spinner';

  import { BizCardsService } from '@client/services/bizcards';
  import { GroupsService } from '@client/services/groups';

  import { generateUUID } from '@common/utils/strings';

  import {
    additions,
    applyMode,
    applyState,
    checked,
    removals,
    selectedGroups,
    targetGroupForAdditions,
    userGroupMap
  } from '../stores/pusher';

  let users = [];
  let loadingUsers = true;
  let loadingGroup = false;

  let groupOptions = [];
  let dynamicGroupNames = new Set();
  let loadingGroups = true;

  /** @type {string} */
  let search;
  let showAll = false;
  let ready = false;

  onMount(async () => {
    const [usersResponse, groupsResponse] = await Promise.all([
      BizCardsService.getUsers({ perPage: 10000 }),
      GroupsService.getAll()
    ]);

    users = usersResponse?.data?.data || usersResponse?.data || [];

    const groups = Array.isArray(groupsResponse) ? groupsResponse : [];
    groupOptions = groups
      .map((group) => ({ label: group.groupname, value: group.groupname }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Dynamic groups derive membership from an internal query (non-empty userAttrs),
    // so their membership can change. Track their names to flag them in the badge.
    dynamicGroupNames = new Set(groups.filter((group) => group.userAttrs?.length).map((group) => group.groupname));

    loadingUsers = false;
    loadingGroups = false;
    // Seed prevGroupNames before flipping `ready` so the reactive block at line ~61
    // treats the restored store value as already-applied (no redundant refetch).
    prevGroupNames = new Set(get(selectedGroups).map((g) => g.value));
    ready = true;
  });

  let prevGroupNames = new Set();

  $: if (ready) {
    const currentNames = new Set(($selectedGroups || []).map((g) => g.value));
    const same = currentNames.size === prevGroupNames.size && [...currentNames].every((n) => prevGroupNames.has(n));
    if (!same) {
      handleGroupsChange(prevGroupNames, currentNames);
      prevGroupNames = currentNames;
    }
  }

  const fetchGroupUsers = async (groupNames) => {
    loadingGroup = true;
    const responses = await Promise.all(
      groupNames.map(async (name) => {
        const response = await BizCardsService.getUsersByGroups([name]);
        const groupUsers = response?.data?.data || response?.data || [];
        return { name, emails: groupUsers.map((u) => u.email) };
      })
    );
    loadingGroup = false;

    userGroupMap.update((map) => {
      for (const { name, emails } of responses) {
        for (const email of emails) {
          if (!map.has(email)) map.set(email, new Set());
          map.get(email).add(name);
        }
      }
      return map;
    });
    return responses;
  };

  const handleGroupsChange = async (prevNames, currentNames) => {
    const added = [...currentNames].filter((n) => !prevNames.has(n));
    const removed = [...prevNames].filter((n) => !currentNames.has(n));

    if (removed.length) {
      userGroupMap.update((map) => {
        for (const name of removed) {
          for (const [email, groups] of map) {
            groups.delete(name);
            if (!groups.size) {
              map.delete(email);
              checked.update((set) => {
                set.delete(email);
                return set;
              });
            }
          }
        }
        return map;
      });
    }

    if (added.length) {
      const responses = await fetchGroupUsers(added);
      checked.update((set) => {
        for (const { emails } of responses) {
          for (const email of emails) set.add(email);
        }
        return set;
      });
    }

    if (!currentNames.size) {
      checked.set(new Set());
      userGroupMap.set(new Map());
    }
  };

  $: visibleUsers = showAll ? users : users.filter((u) => $checked.has(u.email));

  $: filteredUsers =
    search?.length > 1
      ? visibleUsers.filter((row) =>
          Object.values(row).some(
            (value) => typeof value === 'string' && value.toLowerCase().includes(search.toLowerCase())
          )
        )
      : visibleUsers;

  $: allChecked = filteredUsers.length > 0 && filteredUsers.every((u) => $checked.has(u.email));

  /** @param {string} email */
  const toggle = (email) => {
    checked.update((set) => {
      set.has(email) ? set.delete(email) : set.add(email);
      return set;
    });
  };

  const toggleAll = () => {
    checked.update((set) => {
      if (allChecked) {
        for (const u of filteredUsers) set.delete(u.email);
      } else {
        for (const u of filteredUsers) set.add(u.email);
      }
      return set;
    });
  };
</script>

{#if loadingUsers}
  <div class="text-center p-4">
    <Spinner color="primary" />
  </div>
{:else}
  <div class="d-flex align-items-center mb-3">
    <Select
      items={groupOptions}
      value={$selectedGroups}
      on:input={(e) => selectedGroups.set(e.detail)}
      on:clear={() => selectedGroups.set([])}
      placeholder={$format('label.PLACEHOLDER.SELECT_GROUP')}
      loading={loadingGroups}
      clearable={false}
      multiple
      showChevron
      --input-padding="0"
      --internal-padding="0"
      --multi-select-padding="0"
      --value-container-padding="0"
    />
    <Input
      type="checkbox"
      bind:checked={showAll}
      label={$format('label.BIZCARD.ASSIGN.SHOW_ALL_USERS')}
      class="ms-3 m-0 text-nowrap"
    />
  </div>

  {#if showAll || filteredUsers.length || search || loadingGroup}
    <div class="mb-2 d-flex align-items-center">
      <SearchFilter bind:search />
      {#if filteredUsers.length}
        <small class="text-muted ms-auto">
          {$format('label.BIZCARD.ASSIGN.SELECTED_COUNT', { values: { checked: $checked.size, total: users.length } })}
        </small>
      {/if}
    </div>
    <div class="scrollable">
      <Table rows={filteredUsers} size="sm" let:row>
        <Column width="2.5rem">
          <div slot="header">
            <Input type="checkbox" checked={allChecked} class="m-0" on:change={toggleAll} />
          </div>
          <Input type="checkbox" checked={$checked.has(row.email)} class="m-0" on:change={() => toggle(row.email)} />
        </Column>
        <Column header={$format('label.USERNAME')}>
          <a role="button" on:click={() => toggle(row.email)}>{row.email}</a>
        </Column>
        <Column header={$format('label.BIZCARD.ASSIGN.GROUPS')}>
          {#if $userGroupMap.has(row.email)}
            {#each [...$userGroupMap.get(row.email)] as group}
              <Badge color="light" class="me-2">
                {group}
                {#if dynamicGroupNames.has(group)}
                  {@const tooltipId = `dynamic-group-${generateUUID()}`}
                  <span id={tooltipId}>
                    <Icon name="zap" />
                  </span>
                  <Tooltip target={tooltipId} placement="top">
                    {$format('label.DYNAMIC_GROUP')}
                  </Tooltip>
                {/if}
              </Badge>
            {/each}
          {/if}
        </Column>
      </Table>
      <Loading backdrop show={loadingGroup} />
    </div>

    {#if $applyState === 'mixed'}
      <div class="apply-mode-panel mt-3 alert alert-primary">
        <p class="fw-medium mb-2">{$format('label.BIZCARD.ASSIGN.MANUAL_SELECTIONS_HEADING')}</p>

        <div class="form-check">
          <input class="form-check-input" type="radio" id="apply-mode-modify" value="modify" bind:group={$applyMode} />
          <label class="form-check-label" for="apply-mode-modify">
            {$format('label.BIZCARD.ASSIGN.APPLY_MODE_MODIFY')}
          </label>
          {#if $applyMode === 'modify'}
            <ul class="mt-1 mb-0 small">
              {#if $additions.length > 0}
                <li class="d-flex align-items-center gap-2 flex-wrap">
                  <span
                    >{$format('label.BIZCARD.ASSIGN.ADDITIONS_SUMMARY', { values: { count: $additions.length } })}</span
                  >
                  {#if $selectedGroups.length >= 2}
                    <span>→</span>
                    <select class="form-select form-select-sm w-auto" bind:value={$targetGroupForAdditions}>
                      <option value={null}>{$format('label.BIZCARD.ASSIGN.PICK_TARGET_GROUP')}</option>
                      {#each $selectedGroups as group}
                        <option value={group.value}>{group.value}</option>
                      {/each}
                    </select>
                  {/if}
                </li>
              {/if}
              {#if $removals.size > 0}
                <li>{$format('label.BIZCARD.ASSIGN.REMOVALS_SUMMARY', { values: { count: $removals.size } })}</li>
              {/if}
            </ul>
          {/if}
        </div>

        <div class="form-check mt-2">
          <input class="form-check-input" type="radio" id="apply-mode-create" value="create" bind:group={$applyMode} />
          <label class="form-check-label" for="apply-mode-create">
            {$format('label.BIZCARD.ASSIGN.APPLY_MODE_CREATE')}
          </label>
          {#if $applyMode === 'create'}
            <ul class="mt-1 mb-0 small">
              <li>{$format('label.BIZCARD.ASSIGN.NEW_GROUP_SUMMARY', { values: { count: $checked.size } })}</li>
            </ul>
          {/if}
        </div>
      </div>
    {/if}
  {/if}
{/if}

<style>
  .scrollable {
    max-height: 40vh;
    overflow-y: auto;
  }
</style>
